import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Gamepad2,
  Target,
  Award,
  Trophy,
  User,
  Menu,
  X,
  Flame,
  type LucideIcon,
} from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';

// ─── Mock user data (will be replaced by real API data) ──────────────────────
const MOCK_USER = {
  name: 'Marco Rossi',
  level: 12,
  streak: 5,
  points: 12450,
  nextLevelPoints: 13000,
};

// ─── Default nav items (can be overridden via prop) ──────────────────────────
// Supports both direct routes (/loyalty/*) and CMS-based routes (/p/loyalty-*)
const DEFAULT_NAV_ITEMS = [
  { to: '/loyalty/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/loyalty/games', label: 'Giochi & Sfide', icon: Gamepad2 },
  { to: '/loyalty/contests', label: 'Contest', icon: Target },
  { to: '/loyalty/badges', label: 'I Miei Badge', icon: Award },
  { to: '/loyalty/leaderboard', label: 'Classifica', icon: Trophy },
  { to: '/loyalty/profile', label: 'Profilo', icon: User },
];

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface LoyaltySidebarLayoutProps {
  children: React.ReactNode;
  /** Optional override for nav items (e.g. from CMS nav of the loyalty space) */
  navItems?: NavItem[];
}

function SidebarContent({
  navItems,
  branding,
  onNavClick,
}: {
  navItems: NavItem[];
  branding: ReturnType<typeof useBranding>;
  onNavClick: () => void;
}) {
  const progress = Math.min(
    100,
    Math.round((MOCK_USER.points / MOCK_USER.nextLevelPoints) * 100),
  );

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'flex items-center gap-4 h-[52px] pl-4 rounded-2xl w-full text-base font-semibold transition-all duration-150 border',
      isActive
        ? 'bg-[rgba(0,188,125,0.1)] border-[rgba(0,188,125,0.2)] text-[#00d492]'
        : 'text-[#90a1b9] hover:bg-white/5 border-transparent',
    ].join(' ');

  return (
    <div className="flex flex-col h-full">
      {/* ── Logo ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-8 pt-8">
        <div className="w-10 h-10 bg-[#00bc7d] rounded-[14px] flex items-center justify-center shrink-0 shadow-[0_10px_15px_0_rgba(0,188,125,0.2),0_4px_6px_0_rgba(0,188,125,0.2)]">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.name}
              className="w-6 h-6 object-contain"
            />
          ) : (
            <span className="text-[#020618] font-black text-sm">
              {branding.name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div>
          <p className="text-white font-extrabold text-xl leading-7 tracking-tight whitespace-nowrap">
            {branding.name || 'Lottomatica'}
          </p>
          <p className="text-[#00d492] font-bold text-[10px] uppercase tracking-[1.12px]">
            Premium Club
          </p>
        </div>
      </div>

      {/* ── Nav items ────────────────────────────────────────────────────── */}
      <nav className="flex flex-col gap-2 px-4 mt-[64px] flex-1 overflow-y-auto">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} end className={navLinkClass} onClick={onNavClick}>
            <Icon size={20} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* ── User card ────────────────────────────────────────────────────── */}
      <div className="mx-4 mb-4 mt-4 bg-[rgba(2,6,24,0.5)] border border-white/5 rounded-2xl p-4 shrink-0">
        <div className="flex items-center gap-3 mb-3">
          {/* Avatar with initials */}
          <div className="w-10 h-10 border-2 border-[#00bc7d] rounded-full bg-[#1d293d] flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-xs">
              {MOCK_USER.name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white font-bold text-sm leading-5 truncate">{MOCK_USER.name}</p>
            <div className="flex items-center gap-1">
              <p className="text-[#90a1b9] text-xs">Livello {MOCK_USER.level}</p>
              <Flame size={12} className="text-orange-400 shrink-0" />
              <p className="text-[#90a1b9] text-xs">{MOCK_USER.streak}</p>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative h-1.5 rounded-full bg-[#0f172b] overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#00bc7d] to-[#9ae600] transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Points label */}
        <p className="text-right text-[#62748e] text-[10px] font-medium mt-1.5 tracking-wide">
          {MOCK_USER.points.toLocaleString('it-IT')} PTS
        </p>
      </div>
    </div>
  );
}

export function LoyaltySidebarLayout({ children, navItems }: LoyaltySidebarLayoutProps) {
  const branding = useBranding();
  const [mobileOpen, setMobileOpen] = useState(false);
  const resolvedNavItems = navItems ?? DEFAULT_NAV_ITEMS;

  return (
    <div className="min-h-screen bg-[#020618] flex">
      {/* ── Desktop sidebar ──────────────────────────────────────────────── */}
      <aside className="hidden md:flex flex-col w-[288px] bg-[rgba(15,23,43,0.5)] border-r border-white/[0.05] shrink-0 sticky top-0 h-screen overflow-y-auto">
        <SidebarContent
          navItems={resolvedNavItems}
          branding={branding}
          onNavClick={() => {}}
        />
      </aside>

      {/* ── Mobile sidebar overlay ───────────────────────────────────────── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <aside className="relative z-50 flex flex-col w-[288px] bg-[#0f172b] h-full overflow-y-auto border-r border-white/[0.05] shadow-2xl">
            <button
              className="absolute top-4 right-4 p-1.5 text-[#90a1b9] hover:text-white transition-colors"
              onClick={() => setMobileOpen(false)}
              aria-label="Chiudi menu"
            >
              <X size={20} />
            </button>
            <SidebarContent
              navItems={resolvedNavItems}
              branding={branding}
              onNavClick={() => setMobileOpen(false)}
            />
          </aside>
        </div>
      )}

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main className="flex-1 min-h-screen overflow-y-auto">
        {/* Mobile hamburger */}
        <button
          className="md:hidden fixed top-4 left-4 z-30 p-2 bg-[#0f172b] border border-white/10 rounded-xl text-[#90a1b9] hover:text-white transition-colors shadow-lg"
          onClick={() => setMobileOpen(true)}
          aria-label="Apri menu"
        >
          <Menu size={20} />
        </button>

        {children}
      </main>
    </div>
  );
}
